import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DatePicker, Select, Spin } from "antd";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import {
  fetchDemoBookingDetail,
  updateDemoStatus,
  rescheduleDemo,
} from "../services/demoBookingService";
import { useFollowUpOptions } from "../hooks/useManageEnquiry";

const { Option } = Select;

/* ─── Helpers ─── */
const DEMO_STATUS = {
  pending:   { bg: "#fffbeb", color: "#d97706", border: "#fde68a", label: "Pending" },
  confirmed: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "Confirmed" },
  done:      { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Done" },
  cancelled: { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db", label: "Cancelled" },
};

/* ─── Sub-components ─── */
function StatusBadge({ status, size = "sm" }) {
  const s = DEMO_STATUS[status] || DEMO_STATUS.pending;
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, padding: size === "sm" ? "3px 10px" : "4px 14px", borderRadius: 20, fontSize: size === "sm" ? 12 : 13, fontWeight: 700, textTransform: "capitalize", whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

function ModeBadge({ mode, small }) {
  const isVideo = mode === "video";
  return (
    <span style={{ background: isVideo ? "#eff6ff" : "#f0fdf4", color: isVideo ? "#2563eb" : "#16a34a", border: isVideo ? "1px solid #bfdbfe" : "1px solid #bbf7d0", padding: small ? "2px 8px" : "3px 10px", borderRadius: 20, fontSize: small ? 11 : 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
      {isVideo ? "🎥" : "📞"} {isVideo ? "Video" : "Phone"}
    </span>
  );
}
function Field({ label, value, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined, marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: value ? "#111827" : "#c4c4c4", fontWeight: 500, wordBreak: "break-word" }}>
        {value || <span style={{ fontStyle: "italic", color: "#c4c4c4" }}>
          {label === "Company" ? "No company" : label === "Assigned To" ? "Not assigned" : label === "Next Follow-up" ? "Not set" : "—"}
        </span>}
      </div>
    </div>
  );
}

export default function DemoBookingDetail() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [showRemarks, setShowRemarks] = useState(false);
  const [remarks, setRemarks]         = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [reDate, setReDate]           = useState(null);
  const [reMode, setReMode]           = useState("video");
  const [reRemarks, setReRemarks]     = useState("");
  const [reLink, setReLink]           = useState("");

  /* Confirm modal — needed so a video demo can carry a meeting link */
  const [showConfirm, setShowConfirm]     = useState(false);
  const [confirmLink, setConfirmLink]     = useState("");
  const [confirmRemarks, setConfirmRemarks] = useState("");
  // Only "history" now — follow-ups/emails live on the Communication page
  const [activeTab, setActiveTab]     = useState("history");

  const { data: fuOptsRaw = [] } = useFollowUpOptions();
  const fuOpts = Array.isArray(fuOptsRaw) ? fuOptsRaw : (fuOptsRaw?.data || []);

  /* ── Lead status values resolved from the options master by NAME ───────────
     Never hardcode the numeric value: option ids can change in the DB. If a
     name isn't found we send no `status` at all and warn, so the demo_status
     update still goes through instead of writing a wrong lead status.        */
  const normName = (s) => (s || "").toString().trim().toLowerCase();
  const statusValueByName = (name) => {
    const opt = fuOpts.find(
      (o) =>
        normName(o.label) === normName(name) ||
        normName(o.name) === normName(name) ||
        normName(o.option_label) === normName(name),
    );
    if (!opt) {
      console.warn(
        `[DemoBooking] Lead status option "${name}" not found in /lead-follow-up-options — status will be omitted from the payload.`,
      );
      return undefined;
    }
    return opt.option_value ?? opt.value ?? opt.id;
  };
  const STATUS_MEETING_SCHEDULED = statusValueByName("Meeting Scheduled");
  const STATUS_CONVERTED         = statusValueByName("Converted");
  const STATUS_NEGATIVE          = statusValueByName("Negative");

  const { data: response, isLoading, isError } = useQuery({
    queryKey: ["demo-booking-detail", lead_id],
    queryFn:  () => fetchDemoBookingDetail(lead_id),
  });

  const detail   = response?.data      || {};
  const demoLogs = response?.demo_logs || [];
  const followUps = response?.follow_ups || [];

  /* ── helpers (defined early so Field can use them) ── */
  const fmtDate     = (d) => d ? dayjs(d).format("DD-MM-YYYY") : null;
  const fmtDateTime = (d) => d ? dayjs(d).format("DD-MM-YYYY HH:mm") : null;

  /* derive next follow-up date: prefer detail field, fallback to latest follow-up entry */
  const nextFollowUp = (() => {
    if (detail.next_followup_date) return fmtDate(detail.next_followup_date);
    const withDates = followUps
      .filter((fu) => fu.next_followup_date)
      .sort((a, b) => new Date(b.next_followup_date) - new Date(a.next_followup_date));
    return withDates[0] ? fmtDate(withDates[0].next_followup_date) : null;
  })();

  // React Query v5 takes a filters OBJECT — the old array form is a silent
  // no-op, which is why the detail wasn't refetching after an action.
  const inv = () => qc.invalidateQueries({ queryKey: ["demo-booking-detail", lead_id] });

  const statusMut = useMutation({
    mutationFn: (p) => updateDemoStatus(p),
    onSuccess: (res) => {
      toast.success(res?.message || "Status updated");
      setRemarks(""); setShowRemarks(false);
      setShowConfirm(false); setConfirmLink(""); setConfirmRemarks("");
      inv();
    },
    onError:   (e) => toast.error(e?.response?.data?.message || e?.message || "Failed"),
  });

  const reMut = useMutation({
    mutationFn: (p) => rescheduleDemo(p),
    onSuccess: (res) => {
      toast.success(res?.message || "Demo rescheduled successfully");
      setReDate(null); setReMode("video"); setReRemarks(""); setReLink(""); setShowReschedule(false);
      inv();
    },
    onError: (e) => toast.error(e?.response?.data?.message || e?.message || "Failed to reschedule"),
  });

  /* meeting_link is optional but must look like a real URL when provided */
  const isValidUrl = (v) => /^https?:\/\/\S+$/i.test((v || "").trim());
  const isVideoDemo = detail.demo_mode === "video";

  /**
   * Back always goes to the demo bookings list.
   *
   * navigate(-1) was wrong here: going detail → Communication → back lands on
   * this page again, so the previous history entry is the Communication page —
   * pressing Back would bounce straight back into it.
   */
  const goBackToList = () => navigate("/leads/demo-booking");

  /**
   * demo_status is fixed (pending | confirmed | done | cancelled).
   * `status` is the lead status value resolved from the options master, and is
   * omitted entirely when it couldn't be resolved.
   */
  function doStatus(demoStatus, { status, meetingLink, remarksText } = {}) {
    const payload = { lead_id, demo_status: demoStatus };
    if (status != null && status !== "") payload.status = status;
    if (meetingLink) payload.meeting_link = meetingLink.trim();
    const rk = (remarksText ?? remarks ?? "").trim();
    if (rk) payload.remarks = rk;
    statusMut.mutate(payload);
  }

  /* Mark Done converts the lead — confirm before firing */
  function doMarkDone() {
    Swal.fire({
      title: "Mark demo as Done?",
      html: `Demo for <strong>${detail.name || "this lead"}</strong> will be marked as completed and the lead status set to <strong>Converted</strong>.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Done",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
      reverseButtons: true,
    }).then((r) => {
      if (r.isConfirmed) doStatus("done", { status: STATUS_CONVERTED });
    });
  }

  /* Cancel also hits update-demo-status and writes the Negative status to the
     lead — same weight as Mark Done, so it gets the same confirmation. */
  function doCancelDemo() {
    Swal.fire({
      title: "Cancel this demo?",
      html: `Demo for <strong>${detail.name || "this lead"}</strong> will be cancelled and the lead status set to <strong>Negative</strong>.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel Demo",
      cancelButtonText: "Keep it",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    }).then((r) => {
      if (r.isConfirmed) doStatus("cancelled", { status: STATUS_NEGATIVE });
    });
  }

  function doConfirm() {
    const link = confirmLink.trim();
    if (isVideoDemo && link && !isValidUrl(link)) {
      toast.error("Enter a valid meeting link starting with http:// or https://");
      return;
    }
    doStatus("confirmed", {
      status: STATUS_MEETING_SCHEDULED,
      meetingLink: isVideoDemo ? link : "",
      remarksText: confirmRemarks || remarks,
    });
  }

  function doReschedule() {
    if (!reDate) { toast.error("Select date and time"); return; }
    const link = reLink.trim();
    if (reMode === "video" && link && !isValidUrl(link)) {
      toast.error("Enter a valid meeting link starting with http:// or https://");
      return;
    }
    reMut.mutate({
      lead_id,
      demo_date: reDate.format("YYYY-MM-DD HH:mm:ss"),
      demo_mode: reMode,
      status: STATUS_MEETING_SCHEDULED,
      meeting_link: reMode === "video" ? link : "",
      remarks: reRemarks.trim() || undefined,
    });
  }

  /* ── Loaders ── */
  if (isLoading) return (
    <div className="main-content"><div className="page-content">
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}><Spin size="large" /></div>
    </div></div>
  );
  if (isError) return (
    <div className="main-content"><div className="page-content">
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#ef4444" }}>Failed to load</div>
        <button onClick={() => navigate("/leads/demo-booking")} style={{ marginTop: 16, padding: "8px 20px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", cursor: "pointer" }}>Go Back</button>
      </div>
    </div></div>
  );

  // React Query v5 renamed isLoading → isPending for mutations; the old flag is
  // always undefined, so buttons never showed a busy state.
  const busy = statusMut.isPending;

  // "confirmed" = meeting locked in, "done" = demo happened / lead converted.
  // Both are past the point of confirming, so Confirm stays greyed out for them.
  const confirmDisabled =
    detail.demo_status === "confirmed" || detail.demo_status === "done";

  /* ── Shared button style ── */
  const actionBtn = (bg, disabled, shadow) => ({
    padding: "7px 16px", borderRadius: 8, border: "none", background: disabled ? "#e5e7eb" : bg,
    color: disabled ? "#9ca3af" : "#fff", fontSize: 13, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap",
    boxShadow: (!disabled && shadow) ? shadow : "none",
    transition: "opacity 0.15s",
  });

  return (
    <div className="main-content">
    <div className="page-content">
    <div className="container-fluid" style={{ paddingBottom: 40 }}>

      {/* ══════ STICKY HEADER ══════ */}
      <div style={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "10px 16px", marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>

          {/* Left: back + name + badges */}
          <button onClick={goBackToList}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
            ← Back
          </button>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#111827", flexShrink: 0 }}>{detail.name || "Lead"}</div>
          <span style={{ color: "#d1d5db" }}>·</span>
          <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 500, flexShrink: 0 }}>Lead #{lead_id}</span>
          {detail.demo_status && <StatusBadge status={detail.demo_status} />}
          {detail.stage_name && (
            <span style={{ background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
              {detail.stage_name}
            </span>
          )}

          {/* Right: action buttons */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {showRemarks && (
              <input
                value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder="Remarks (optional)"
                style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, outline: "none", width: 200 }}
              />
            )}
            <button onClick={() => setShowReschedule(true)}
              style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              🗓 Reschedule
            </button>
            <button onClick={() => setShowRemarks((v) => !v)}
              style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: showRemarks ? "#f3f4f6" : "#fff", color: "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              📝 Remarks
            </button>
            {/* Confirm is only meaningful while the demo is still upcoming. Once it
                is done (lead converted) there is nothing left to confirm, so keep
                the button plain instead of letting it light up again. */}
            <button
              onClick={() => { setConfirmLink(detail.demo_meeting_link || ""); setConfirmRemarks(remarks); setShowConfirm(true); }}
              disabled={busy || confirmDisabled}
              style={actionBtn("linear-gradient(135deg,#16a34a,#15803d)", busy || confirmDisabled, "0 2px 8px rgba(22,163,74,.3)")}>
              ✓ Confirm
            </button>
            <button onClick={doMarkDone} disabled={busy || detail.demo_status === "done"}
              style={actionBtn("linear-gradient(135deg,#2563eb,#1d4ed8)", busy || detail.demo_status === "done", "0 2px 8px rgba(37,99,235,.3)")}>
              {busy ? "Saving…" : "✔ Mark Done"}
            </button>
            <button onClick={doCancelDemo} disabled={busy || detail.demo_status === "cancelled"}
              style={actionBtn("#6b7280", busy || detail.demo_status === "cancelled", null)}>
              ✕ Cancel
            </button>
          </div>
        </div>
      </div>

      {/* ══════ BODY — 2-column grid ══════ */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 18, alignItems: "start" }}>

        {/* ── LEFT COLUMN ── */}
        <div>

          {/* Lead Information */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "11px 18px", borderBottom: "1px solid #f0f0f0", background: "#f9fafb", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 3, height: 16, borderRadius: 3, background: "#6366f1" }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1f2937" }}>Lead Information</span>
            </div>
            <div style={{ padding: "16px 18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 28px" }}>
                <Field label="Name"            value={detail.name} />
                <Field label="Company"         value={detail.company} />
                <Field label="Email"           value={detail.email} />
                <Field label="Phone"           value={detail.phone} />
                <Field label="Attorney Number" value={detail.attorney_number} />
                <Field label="Assigned To"     value={detail.assign_to_name ? `${detail.assign_to_name}${detail.assign_to_email ? ` (${detail.assign_to_email})` : ""}` : null} />
                <Field label="Next Follow-up"  value={nextFollowUp} />
                {detail.message && <Field label="Message" value={detail.message} full />}
              </div>
            </div>
          </div>

          {/* Reschedule modal rendered at root level below */}
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div>

          {/* Current Demo highlight card */}
          <div style={{ borderRadius: 12, border: "1px solid #c4b5fd", boxShadow: "0 4px 14px rgba(109,40,217,0.12)", overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "11px 18px", background: "linear-gradient(135deg,#4f46e5 0%,#7c3aed 100%)" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>📅 Current Demo</span>
            </div>
            <div style={{ padding: "14px 18px", background: "#fff" }}>
              {detail.demo_date_formatted ? (
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 4 }}>Scheduled</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", letterSpacing: -0.5, marginBottom: 10 }}>{detail.demo_date_formatted}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <ModeBadge mode={detail.demo_mode} />
                    <StatusBadge status={detail.demo_status} />
                  </div>
                </div>
              ) : (
                <div style={{ color: "#c4c4c4", fontSize: 13, fontStyle: "italic" }}>No demo scheduled yet</div>
              )}
            </div>
          </div>

          {/* Tabs: Demo History | Follow-ups */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", overflow: "hidden" }}>

            {/* Tab bar — "Communication" is a link, not a tab: it opens the shared
                EnquiryFollowUp page (Compose Mail / Call / Note / Inbox) for this
                lead and passes the origin so its Back returns here. */}
            <div style={{ display: "flex", borderBottom: "1px solid #f0f0f0" }}>
              <button onClick={() => setActiveTab("history")}
                style={{ flex: 1, padding: "10px 12px", border: "none", borderBottom: activeTab === "history" ? "2px solid #6366f1" : "2px solid transparent", background: activeTab === "history" ? "#fafbff" : "#f9fafb", color: activeTab === "history" ? "#4f46e5" : "#6b7280", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                Demo History
                <span style={{ background: activeTab === "history" ? "#e0e7ff" : "#f0f0f0", color: activeTab === "history" ? "#4338ca" : "#9ca3af", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
                  {demoLogs.length}
                </span>
              </button>

              <button
                onClick={() =>
                  navigate(`/leads/${lead_id}/followup?from=${encodeURIComponent(`/leads/demo-booking/${lead_id}`)}`, {
                    state: { from: `/leads/demo-booking/${lead_id}` },
                  })
                }
                title="Open emails, calls and notes for this lead"
                style={{ flex: 1, padding: "10px 12px", border: "none", borderBottom: "2px solid transparent", background: "#f9fafb", color: "#6b7280", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fafbff"; e.currentTarget.style.color = "#4f46e5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.color = "#6b7280"; }}
              >
                💬 Communication
                <span style={{ background: "#f0f0f0", color: "#9ca3af", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
                  {followUps.length}
                </span>
                <span style={{ fontSize: 11, color: "#9ca3af" }}>↗</span>
              </button>
            </div>

            {/* ── Demo History tab ── */}
            {activeTab === "history" && (
              <div style={{ maxHeight: 320, overflowY: "auto", padding: "12px 16px" }}>
                {demoLogs.length === 0 ? (
                  <div style={{ color: "#c4c4c4", fontSize: 13, fontStyle: "italic", textAlign: "center", padding: "24px 0" }}>No previous bookings.</div>
                ) : (
                  <div style={{ position: "relative", paddingLeft: 22 }}>
                    <div style={{ position: "absolute", left: 6, top: 6, bottom: 6, width: 2, background: "#e0e7ff", borderRadius: 2 }} />
                    {demoLogs.map((log, idx) => (
                      <div key={log.id || idx} style={{ position: "relative", marginBottom: 14, paddingBottom: 14, borderBottom: idx < demoLogs.length - 1 ? "1px solid #f9fafb" : "none" }}>
                        <div style={{ position: "absolute", left: -19, top: 5, width: 9, height: 9, borderRadius: "50%", background: "#4f46e5", border: "2px solid #c7d2fe" }} />
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#1f2937" }}>{log.demo_date_formatted}</span>
                          <ModeBadge mode={log.demo_mode} small />
                        </div>
                        {log.booked_by_name && (
                          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>by <span style={{ color: "#374151", fontWeight: 600 }}>{log.booked_by_name}</span></div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
      {/* ══════ CONFIRM DEMO MODAL ══════ */}
      {showConfirm && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setShowConfirm(false); setConfirmLink(""); } }}
          style={{ position: "fixed", inset: 0, zIndex: 1300, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>

            {/* Header */}
            <div style={{ padding: "16px 22px", background: "linear-gradient(135deg,#16a34a,#15803d)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>✓ Confirm Demo</div>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, marginTop: 2 }}>
                  Customer gets a demo confirmation email
                </div>
              </div>
              <button onClick={() => { setShowConfirm(false); setConfirmLink(""); }}
                style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "none", color: "#fff", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            {/* Body */}
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 13, color: "#374151" }}>
                <span style={{ fontWeight: 600 }}>Scheduled:</span>
                <span>{fmtDateTime(detail.demo_date) || "—"}</span>
                <ModeBadge mode={detail.demo_mode} small />
              </div>

              {/* Meeting link — video mode only */}
              {isVideoDemo && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
                    Meeting Link (optional)
                  </div>
                  <input
                    value={confirmLink} onChange={(e) => setConfirmLink(e.target.value)}
                    placeholder="https://meet.google.com/… or https://zoom.us/…"
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 8,
                      border: `1px solid ${confirmLink && !isValidUrl(confirmLink) ? "#fca5a5" : "#d1d5db"}`,
                      fontSize: 13, outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <div style={{ fontSize: 11, color: confirmLink && !isValidUrl(confirmLink) ? "#dc2626" : "#9ca3af", marginTop: 4 }}>
                    {confirmLink && !isValidUrl(confirmLink)
                      ? "Must start with http:// or https://"
                      : "Added as a “Join Video Meeting” button in the confirmation email"}
                  </div>
                </div>
              )}

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Remarks (optional)</div>
                <textarea
                  value={confirmRemarks} onChange={(e) => setConfirmRemarks(e.target.value)}
                  rows={2} placeholder="Any note for this confirmation…"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, resize: "none", outline: "none", boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.5 }}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "14px 22px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowConfirm(false); setConfirmLink(""); }}
                style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={doConfirm} disabled={busy}
                style={{ padding: "8px 24px", borderRadius: 8, border: "none", background: busy ? "#e5e7eb" : "linear-gradient(135deg,#16a34a,#15803d)", color: busy ? "#9ca3af" : "#fff", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", boxShadow: busy ? "none" : "0 2px 8px rgba(22,163,74,.3)" }}>
                {busy ? <><Spin size="small" /> &nbsp;Saving…</> : "✓ Confirm Demo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════ RESCHEDULE MODAL ══════ */}
      {showReschedule && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setShowReschedule(false); setReDate(null); setReRemarks(""); } }}
          style={{ position: "fixed", inset: 0, zIndex: 1300, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden" }}>

            {/* Modal header */}
            <div style={{ padding: "16px 22px", background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>🗓 Reschedule Demo</div>
                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 2 }}>Status will reset to Pending after rescheduling</div>
              </div>
              <button onClick={() => { setShowReschedule(false); setReDate(null); setReRemarks(""); }}
                style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "none", color: "#fff", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            {/* Modal body */}
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>New Date & Time *</div>
                <DatePicker
                  showTime format="YYYY-MM-DD HH:mm" value={reDate} onChange={setReDate}
                  style={{ width: "100%" }} placeholder="Select date & time"
                  disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Mode</div>
                <Select value={reMode} onChange={setReMode} style={{ width: "100%" }}
                  getPopupContainer={(trigger) => trigger.parentElement}>
                  <Option value="video">🎥 Video</Option>
                  <Option value="phone">📞 Phone</Option>
                </Select>
              </div>
              {/* Meeting link — video mode only */}
              {reMode === "video" && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
                    Meeting Link (optional)
                  </div>
                  <input
                    value={reLink} onChange={(e) => setReLink(e.target.value)}
                    placeholder="https://meet.google.com/… or https://zoom.us/…"
                    style={{
                      width: "100%", padding: "9px 12px", borderRadius: 8,
                      border: `1px solid ${reLink && !isValidUrl(reLink) ? "#fca5a5" : "#d1d5db"}`,
                      fontSize: 13, outline: "none", boxSizing: "border-box",
                    }}
                  />
                  <div style={{ fontSize: 11, color: reLink && !isValidUrl(reLink) ? "#dc2626" : "#9ca3af", marginTop: 4 }}>
                    {reLink && !isValidUrl(reLink)
                      ? "Must start with http:// or https://"
                      : "Added as a “Join Video Meeting” button in the customer email"}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Remarks (optional)</div>
                <textarea
                  value={reRemarks} onChange={(e) => setReRemarks(e.target.value)}
                  rows={2} placeholder="Reason for rescheduling…"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, resize: "none", outline: "none", boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.5 }}
                />
              </div>
            </div>

            {/* Modal footer */}
            <div style={{ padding: "14px 22px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowReschedule(false); setReDate(null); setReRemarks(""); setReLink(""); }}
                style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={doReschedule} disabled={reMut.isPending || !reDate}
                style={{ padding: "8px 24px", borderRadius: 8, border: "none", background: !reDate ? "#e5e7eb" : "linear-gradient(135deg,#f59e0b,#d97706)", color: !reDate ? "#9ca3af" : "#fff", fontSize: 13, fontWeight: 700, cursor: !reDate ? "not-allowed" : "pointer", boxShadow: reDate ? "0 2px 8px rgba(217,119,6,.3)" : "none" }}>
                {reMut.isPending ? <><Spin size="small" /> &nbsp;Saving…</> : "Confirm Reschedule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div></div></div>
  );
}
